/** Instance-owned launch extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamLaunch(runtime) {
  runtime.getRapidLaunchPendingUserBalls = function () {
    return runtime.game.launchSource === 'user' ? runtime.game.launchQueue : 0;
  };
  runtime.getAvailableUserLaunchSlots = function () {
    return Math.max(
      0,
      runtime.USER_LAUNCHED_BALL_CAP -
        runtime.game.activeUserBalls -
        runtime.getRapidLaunchPendingUserBalls(),
    );
  };
  runtime.getLaunchQueueIntervalSeconds = function () {
    return runtime.isRapidLaunchMode()
      ? runtime.RAPID_LAUNCH_QUEUE_INTERVAL_SECONDS
      : runtime.DEFAULT_LAUNCH_QUEUE_INTERVAL_SECONDS;
  };
  runtime.getLaunchVolleySize = function () {
    if (runtime.isChaosLaunchActive()) return Math.max(runtime.game.ballCount, 50);
    return runtime.game.ballCount;
  };
  runtime.launchVolley = function () {
    runtime.recordSpeedIdleInteraction();
    if (runtime.game.status === 'gameover') return;
    if (runtime.game.status === 'paused') return;
    const now = runtime.now();
    runtime.markInteractionBoost();
    const rapidLaunch = runtime.isRapidLaunchMode();
    if (rapidLaunch && !runtime.canAcceptRapidUserLaunch()) return;
    const earlyRelaunch =
      !rapidLaunch && runtime.game.volleyActive && runtime.canRelaunchDuringVolley();
    if (
      (!rapidLaunch && runtime.game.volleyActive && !earlyRelaunch) ||
      runtime.game.launchQueue > 0
    )
      return;
    if (earlyRelaunch) {
      runtime.advanceRoundAfterVolley();
      if (runtime.game.status === 'gameover') return;
      runtime.game.mods.unshift(
        `Early launch at ${Math.round(runtime.getVolleyElapsedSeconds())}s`,
      );
      runtime.game.mods.length = 4;
    }
    const launchSource = runtime.isChaosLaunchActive() ? 'chaos' : 'user';
    let volleySize = runtime.getLaunchVolleySize();
    if (rapidLaunch && launchSource === 'user') {
      volleySize = Math.min(volleySize, runtime.getAvailableUserLaunchSlots());
      if (volleySize <= 0) {
        runtime.syncHud();
        return;
      }
    }
    runtime.game.lastTick = now;
    runtime.game.volleyStartedAt = now;
    if (!rapidLaunch) runtime.game.roundAdvancedForVolley = false;
    if (runtime.currentModeConfig().resetCombosOnLaunch) runtime.resetBallCombos();
    runtime.hideOverlay();
    runtime.game.modeRunStarted = true;
    runtime.game.status = 'playing';
    runtime.game.volleyActive = true;
    runtime.game.launchSource = launchSource;
    runtime.game.launchQueue = Math.max(0, volleySize - 1);
    runtime.game.launchDelay = runtime.getLaunchQueueIntervalSeconds();
    runtime.game.launchFlash = 0.38;
    runtime.spawnBall(runtime.game.angle, 0, { source: runtime.game.launchSource });
    runtime.recordSpeedIdleInteraction({ acceptedNormalShot: true });
    const aim = runtime.trigDeg(runtime.game.angle);
    const muzzleX = runtime.launcher.x + aim.cos * runtime.LAUNCHER_MUZZLE_DISTANCE;
    const muzzleY = runtime.launcher.y - aim.sin * runtime.LAUNCHER_MUZZLE_DISTANCE;
    runtime.spark(muzzleX, muzzleY, '#ffd166', 18);
    runtime.spark(muzzleX, muzzleY, '#ff8b3d', 10);
    runtime.syncModeControls();
    runtime.syncHud();
    runtime.requestDraw();
  };
  runtime.getVolleyElapsedSeconds = function (now = runtime.now()) {
    if (!runtime.game.volleyStartedAt) return 0;
    return Math.max(0, (now - runtime.game.volleyStartedAt) / 1000);
  };
  runtime.hasLaunchWaitElapsed = function (now = runtime.now()) {
    return runtime.getVolleyElapsedSeconds(now) >= runtime.game.launchWaitSeconds;
  };
  runtime.canRelaunchDuringVolley = function (now = runtime.now()) {
    return (
      runtime.game.volleyActive &&
      runtime.game.launchQueue === 0 &&
      runtime.hasLaunchWaitElapsed(now)
    );
  };
  runtime.getLaunchCountdownSeconds = function (now = runtime.now()) {
    if (!runtime.game.volleyActive || runtime.game.status === 'gameover') return 0;
    return Math.max(0, runtime.game.launchWaitSeconds - runtime.getVolleyElapsedSeconds(now));
  };
  runtime.formatLaunchWaitSeconds = function (seconds) {
    return `${Math.max(0, seconds).toFixed(1)}s`;
  };
  runtime.formatLaunchWaitUpgradeValue = function () {
    if (runtime.game.launchWaitSeconds <= runtime.MIN_LAUNCH_WAIT_SECONDS) return 'MAX';
    return runtime.formatLaunchWaitSeconds(runtime.game.launchWaitSeconds);
  };
  runtime.canAcceptRapidUserLaunch = function () {
    if (!runtime.isRapidLaunchMode()) return true;
    if (runtime.isChaosLaunchActive()) return true;
    return runtime.getAvailableUserLaunchSlots() > 0;
  };
  runtime.resetBallCombos = function () {
    for (const ball of runtime.game.balls) {
      ball.combo = 0;
      ball.comboScoreBonus = 0;
    }
  };
  runtime.evictUserLaunchedBallForCap = function () {
    const userBalls = runtime.game.balls.filter((ball) => ball.source === 'user' && !ball.dead);
    const newestUserBallId = Math.max(0, ...userBalls.map((ball) => ball.id || 0));
    const sortCandidates = (candidates) =>
      candidates.sort((a, b) => {
        const speedDelta = Math.hypot(a.vx, a.vy) - Math.hypot(b.vx, b.vy);
        if (Math.abs(speedDelta) > 1) return speedDelta;
        const comboDelta = (a.comboScoreBonus || 0) - (b.comboScoreBonus || 0);
        if (comboDelta) return comboDelta;
        return (a.contributionScore || 0) - (b.contributionScore || 0);
      });
    const eligible = sortCandidates(
      userBalls.filter((ball) => ball.age >= runtime.USER_LAUNCHED_BALL_GRACE_SECONDS),
    );
    const fallback = sortCandidates(userBalls.filter((ball) => ball.id !== newestUserBallId));
    const victim = eligible[0] || fallback[0];
    if (!victim) return false;
    victim.dead = true;
    runtime.spark(victim.x, victim.y, '#ffd166', 10);
    runtime.addFloater(victim.x, victim.y, 'recycled', '#ffe0a4');
    return true;
  };
  runtime.enforceUserLaunchedBallCap = function () {
    if (!runtime.shouldCapUserLaunchedBalls()) return;
    while (runtime.game.activeUserBalls >= runtime.USER_LAUNCHED_BALL_CAP) {
      if (!runtime.evictUserLaunchedBallForCap()) break;
      runtime.game.activeUserBalls = Math.max(0, runtime.game.activeUserBalls - 1);
    }
  };
  runtime.spawnBall = function (angle, split = 0, options = {}) {
    const source = options.source || (split > 0 ? 'split' : 'user');
    if (source === 'user') runtime.enforceUserLaunchedBallCap();
    const speed = 508;
    const aim = runtime.trigDeg(angle);
    const neutron = source === 'neutron' || Boolean(options.neutron);
    const ball = {
      id: runtime.game.nextBallId,
      x: runtime.launcher.x,
      y: runtime.launcher.y,
      vx: aim.cos * speed,
      vy: -aim.sin * speed,
      age: 0,
      baseSpeed: speed,
      maxSpeed: 968 + runtime.game.level * 9,
      sideBounceStreak: 0,
      wallBounceStreak: 0,
      planetCooldowns: {},
      activeGravityPlanetId: null,
      gravityReuseFrames: 0,
      gravityFx: 0,
      gravityFy: 0,
      gravityGlow: 0,
      r: neutron ? 5.6 : 4.5,
      glow: 1,
      power: neutron ? runtime.NEUTRON_BOMB_POWER : 1,
      pierce: split,
      combo: 0,
      comboScoreBonus: 0,
      source,
      neutron,
      neutronDeploymentId: neutron ? runtime.game.neutronDeploymentId : 0,
      contributionScore: 0,
      hue: runtime.rand(0, 360),
      trail: [],
    };
    runtime.game.balls.push(ball);
    if (source === 'user') runtime.game.activeUserBalls += 1;
    runtime.game.nextBallId += 1;
  };
  runtime.canActivateNeutronBombs = function () {
    return (
      runtime.game.status !== 'paused' &&
      runtime.game.status !== 'gameover' &&
      runtime.game.neutronFuel >= runtime.game.neutronFuelCost
    );
  };
  runtime.spawnNeutronBombDeployment = function () {
    if (!runtime.canActivateNeutronBombs()) return false;
    runtime.markInteractionBoost(1200);
    const cost = runtime.game.neutronFuelCost;
    runtime.game.neutronFuel -= cost;
    runtime.game.neutronFuelCost += 1;
    runtime.game.neutronDeployments += 1;
    runtime.recordSpeedIdleInteraction();
    runtime.recordSpecialChain();
    runtime.game.neutronDeploymentId += 1;
    runtime.game.neutronStarChain = 0;
    runtime.game.neutronBombsInDeployment = runtime.NEUTRON_BOMB_DEPLOYMENT_SIZE;
    runtime.game.modeRunStarted = true;
    runtime.game.status = 'playing';
    runtime.game.volleyActive = true;
    runtime.game.launchFlash = 0.7;
    runtime.hideOverlay();
    const center = (runtime.NEUTRON_BOMB_DEPLOYMENT_SIZE - 1) / 2;
    for (let index = 0; index < runtime.NEUTRON_BOMB_DEPLOYMENT_SIZE; index += 1) {
      const spread = (index - center) * 0.62;
      runtime.spawnBall(runtime.game.angle + spread, 0, { source: 'neutron', neutron: true });
    }
    const aim = runtime.trigDeg(runtime.game.angle);
    const muzzleX = runtime.launcher.x + aim.cos * runtime.LAUNCHER_MUZZLE_DISTANCE;
    const muzzleY = runtime.launcher.y - aim.sin * runtime.LAUNCHER_MUZZLE_DISTANCE;
    runtime.spark(muzzleX, muzzleY, '#fff8f0', 54);
    runtime.explode(muzzleX, muzzleY, '#fff4c2', 44, 1.1);
    runtime.addBurst(muzzleX, muzzleY, '#fff8f0', 28, 300, 0.52, 5);
    runtime.game.screenFlash = Math.max(runtime.game.screenFlash, 0.72);
    runtime.game.shake = Math.max(runtime.game.shake, 0.65);
    runtime.game.mods.unshift(`Neutron bombs deployed: ${runtime.NEUTRON_BOMB_DEPLOYMENT_SIZE}`);
    runtime.game.mods.length = 4;
    runtime.syncHud();
    runtime.requestDraw();
    return true;
  };
  runtime.syncNeutronDeploymentState = function () {
    if (!runtime.game.neutronBombsInDeployment) return;
    const liveNeutrons = runtime.game.activeNeutronBombs;
    runtime.game.neutronBombsInDeployment = liveNeutrons;
    if (liveNeutrons > 0) return;
    runtime.game.neutronStarChain = 0;
    runtime.game.mods.unshift('Neutron chain reset');
    runtime.game.mods.length = 4;
  };
  runtime.spawnBurstBall = function (x, y, angle, speed, options = {}) {
    const aim = runtime.trigDeg(angle);
    const hitLimit = Math.max(1, Math.round(runtime.game.level * runtime.rand(2, 10)));
    const power = Math.max(1, Math.floor(options.power || 1));
    runtime.game.balls.push({
      id: runtime.game.nextBallId,
      x: runtime.clamp(x, 12, runtime.W - 12),
      y: runtime.clamp(y, 12, runtime.H - 120),
      vx: aim.cos * speed,
      vy: aim.sin * speed,
      age: 0,
      baseSpeed: speed,
      maxSpeed: 3000,
      sideBounceStreak: 0,
      wallBounceStreak: 0,
      planetCooldowns: {},
      activeGravityPlanetId: null,
      gravityReuseFrames: 0,
      gravityFx: 0,
      gravityFy: 0,
      gravityGlow: 0,
      r: 4.2,
      glow: 1,
      power,
      pierce: 0,
      combo: 0,
      comboScoreBonus: 0,
      temporary: true,
      hitsLeft: hitLimit,
      hue: runtime.rand(0, 360),
      trail: [],
    });
    runtime.game.nextBallId += 1;
  };
  runtime.spendTemporaryHit = function (ball, x = ball.x, y = ball.y) {
    if (!ball.temporary || ball.dead) return;
    ball.hitsLeft -= 1;
    if (ball.hitsLeft > 0) return;
    ball.dead = true;
    runtime.spark(x, y, '#ffd166', 14);
    runtime.addFloater(x, y, 'spent', '#ffd166');
  };
  runtime.updateRuntimeQuality = function () {
    const load = runtime.computeEntityLoad();
    runtime.game.energyLoad = load;
    if (runtime.game.energyMode === 'pressureGuard' || runtime.game.energyMode === 'idle')
      runtime.game.drawQuality = 'low';
    else if (
      runtime.isMobilePressureMode() &&
      (load >= runtime.LOW_POWER_BALL_THRESHOLD * 0.55 || runtime.game.boardShiftCue > 0)
    )
      runtime.game.drawQuality = 'low';
    else if (runtime.isMobilePressureMode() || runtime.game.energyMode === 'balanced')
      runtime.game.drawQuality = 'medium';
    else if (load >= runtime.CHAOS_BALL_THRESHOLD || runtime.game.chaosTurns > 0)
      runtime.game.drawQuality = 'low';
    else if (load >= runtime.LOW_POWER_BALL_THRESHOLD) runtime.game.drawQuality = 'medium';
    else runtime.game.drawQuality = 'high';
  };
  runtime.endRun = function (title, copy) {
    runtime.game.status = 'gameover';
    runtime.game.volleyActive = false;
    runtime.game.launchQueue = 0;
    runtime.prepareGameOverScoreboard();
    runtime.showOverlay('Game Over', title, copy);
    runtime.syncModeControls();
    runtime.syncHud();
  };
  runtime.updateModeTimers = function (dt) {
    if (!runtime.game.modeRunStarted || runtime.game.status !== 'playing') return;
    const modeConfig = runtime.currentModeConfig();
    if (modeConfig.durationSeconds) {
      runtime.game.modeTimerRemaining = Math.max(0, runtime.game.modeTimerRemaining - dt);
      if (runtime.game.modeTimerRemaining <= 0) {
        runtime.endRun(
          'Time',
          'The 2 minute Speed run is complete. Save your score or restart for another rush.',
        );
        return;
      }
      runtime.game.speedTimedPressureElapsed += dt;
      while (
        runtime.game.speedTimedPressureElapsed >= runtime.SPEED_TIMED_PRESSURE_SECONDS &&
        runtime.game.status !== 'gameover'
      ) {
        runtime.game.speedTimedPressureElapsed -= runtime.SPEED_TIMED_PRESSURE_SECONDS;
        runtime.addSpeedPressureRow(false, 'timer');
      }
    }
    if (modeConfig.timedRowAdvanceSeconds) {
      runtime.game.timedRowAdvanceElapsed += dt;
      while (
        runtime.game.timedRowAdvanceElapsed >= runtime.game.timedCurrentRowAdvanceSeconds &&
        runtime.game.status !== 'gameover'
      ) {
        runtime.game.timedRowAdvanceElapsed -= runtime.game.timedCurrentRowAdvanceSeconds;
        runtime.game.roundAdvancedForVolley = false;
        runtime.advanceRoundAfterVolley({ force: true });
        runtime.game.timedCurrentRowAdvanceSeconds = Math.max(
          runtime.TIMED_ROW_ADVANCE_MIN_SECONDS,
          runtime.game.timedCurrentRowAdvanceSeconds - runtime.TIMED_ROW_ACCELERATION_SECONDS,
        );
      }
    }
  };
  runtime.applySpeedBottomPenalty = function () {
    if (!runtime.currentModeConfig().bottomPenalty) return;
    let penalty = 0;
    for (const brick of runtime.game.bricks) {
      if (brick.dead || brick.y < runtime.H) continue;
      const loss = Math.min(
        Math.max(0, runtime.game.score - penalty),
        Math.max(1, Math.floor(brick.hp || 1)) * 100,
      );
      penalty += loss;
      if (loss > 0)
        runtime.addFloater(
          brick.x + brick.w / 2,
          runtime.H - 24,
          `-${runtime.formatBigNumber(loss)}`,
          '#ff8a3d',
          { scale: 1.45, life: 1.4, stroke: 'rgba(42, 6, 0, 0.9)' },
        );
      brick.dead = true;
      runtime.recordBrickDestroyed(brick);
      runtime.game.spatialDirty = true;
    }
    if (!penalty) return;
    runtime.game.score = Math.max(0, runtime.game.score - penalty);
    runtime.ui.score.textContent = runtime.formatPinballNumber(runtime.game.score, 12);
    runtime.game.mods.unshift(`Fallen brick penalty: -${runtime.formatGroupedNumber(penalty)}`);
    runtime.game.mods.length = 4;
    runtime.game.spatialDirty = true;
  };
}
